import React from 'react';
import { Paper, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

const PointPopup = ({ point, position, onClose, view }) => {
    if (!point || !position) return null;

    // Safe access to view dimensions
    const viewportWidth = view ? view.width : window.innerWidth;
    // Estimate popup dimensions
    const pWidth = 240;
    const pHeight = 220;

    const arrowSize = 20;
    // Offset ligeramente menor al tamaño de la flecha para crear superposición
    // y tapar el hueco de las esquinas redondeadas
    const offset = 12;

    const { x, y } = position;

    // Determine horizontal placement
    // Default to Right (placing popup to the right of cursor)
    let placeRight = true;
    if (x + pWidth + offset > viewportWidth) {
        placeRight = false; // Place left
    }

    // Determine vertical placement
    // Default to Top (placing popup above cursor)
    let placeTop = true;
    if (y - pHeight - offset < 0) {
        placeTop = false; // Place bottom
    }

    // Calculate coordinates and transforms based on quadrant
    let left = x;
    let top = y;
    let transform = '';
    let origin = '';

    // Styles for the arrow (pseudo-element)
    let arrowStyle = {
        content: '""',
        position: 'absolute',
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
    };

    if (placeTop) {
        top -= offset;
        if (placeRight) {
            // Quadrant: Top-Right
            left += offset - 8;
            transform = 'translate(0, -100%)';
            origin = 'bottom left';
            arrowStyle = {
                ...arrowStyle,
                bottom: `-${offset}px`,
                left: '0px',
                borderWidth: `${arrowSize}px ${arrowSize}px 0 0`,
                borderTopColor: 'white'
            };
        } else {
            // Quadrant: Top-Left
            left -= offset - 8;
            transform = 'translate(-100%, -100%)';
            origin = 'bottom right';
            arrowStyle = {
                ...arrowStyle,
                bottom: `-${offset}px`,
                right: '0px',
                borderWidth: `${arrowSize}px 0 0 ${arrowSize}px`,
                borderTopColor: 'white'
            };
        }
    } else {
        top += offset;
        if (placeRight) {
            // Quadrant: Bottom-Right
            left += offset - 8;
            transform = 'translate(0, 0)';
            origin = 'top left';
            arrowStyle = {
                ...arrowStyle,
                top: `-${offset}px`,
                left: '0px',
                borderWidth: `0 ${arrowSize}px ${arrowSize}px 0`,
                borderBottomColor: 'white'
            };
        } else {
            // Quadrant: Bottom-Left
            left -= offset - 8;
            transform = 'translate(-100%, 0)';
            origin = 'top right';
            arrowStyle = {
                ...arrowStyle,
                top: `-${offset}px`,
                right: '0px',
                borderWidth: `0 0 ${arrowSize}px ${arrowSize}px`,
                borderBottomColor: 'white'
            };
        }
    }

    return (
        <Paper
            elevation={4}
            sx={{
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                transform: transform,
                transformOrigin: origin,
                zIndex: 2000,
                width: `${pWidth}px`,
                padding: 2,
                backgroundColor: 'white',
                borderRadius: 2,
                animation: 'fadeIn 0.2s ease-out',
                '@keyframes fadeIn': {
                    from: { opacity: 0, transform: `${transform} scale(0.9)` },
                    to: { opacity: 1, transform: `${transform} scale(1)` }
                },
                '&:before': arrowStyle
            }}
        >
            <Box sx={{ position: 'relative' }}>
                <IconButton
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: 'white',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                    onClick={onClose}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Punto {point.index + 1}
                </Typography>
                <Box sx={{ display: 'grid', gap: 0.5 }}>
                    <Typography variant="body2">
                        <strong>Lat:</strong> {Number(point.latitude).toFixed(6)}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Lon:</strong> {Number(point.longitude).toFixed(6)}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Fecha:</strong> {moment(point.timestamp).format('DD/MM/YYYY')}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Hora:</strong> {moment(point.timestamp).format('HH:mm:ss')}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Velocidad:</strong> {Number(point.speed || 0).toFixed(1)} km/h
                    </Typography>
                    <Typography variant="body2">
                        <strong>Rumbo:</strong> {point.heading !== undefined && point.heading !== null ? Number(point.heading).toFixed(1) : 'N/A'}°
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default PointPopup;
